import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useLocation, useHistory, Link } from 'react-router-dom';
import { database, storage } from 'helpers/firebase';
import { getFileIcon, getSizeLabel, getDateFromTs } from 'helpers/functions';
import { customStyles } from 'helpers/constants';
import { useOutsideClick } from 'hooks/useOutsideClick';
import CreatableSelect from 'react-select/creatable';
import Navbar from 'components/organisms/Navbar';
import Sidebar from 'components/organisms/Sidebar';
import Heading from 'components/atoms/Heading';
import Loading from 'components/atoms/Loading';
import Paragraph from 'components/atoms/Paragraph';
import { ReactComponent as Download } from 'assets/icons/download.svg';
import { ReactComponent as Close } from 'assets/icons/close.svg';
import { ReactComponent as Remove } from 'assets/icons/remove.svg';
import { ReactComponent as Edit } from 'assets/icons/edit.svg';
import favicon from '../assets/icons/cloud.ico';

const Container = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  background-color: ${({ theme }) => theme.gray5};
  height: 100vh;
  width: 100%;
  overflow: hidden;
`;

const Row = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  width: 100%;
`;

const Column = styled.div`
  display: flex;
  flex-flow: ${({ reverse }) => (reverse ? 'column-reverse' : 'column')} nowrap;
  align-items: center;
  width: 100%;
`;

const Content = styled.main`
  position: relative;
  display: flex;
  flex-flow: row wrap;
  height: 100%;
  width: 100%;
`;

const Details = styled.aside`
  position: fixed;
  top: 64px;
  right: 0;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: space-between;
  padding: 50px 0 0;
  width: 400px;
  height: calc(100vh - 64px);
  background-color: ${({ theme }) => theme.gray4};
  border-left: 2px solid ${({ theme }) => theme.gray3};
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
  transition: tranform 0.15s ease-in-out, opacity 0.15s ease-in-out;

  ${Heading} {
    text-align: center;
    max-width: 100%;
    word-wrap: break-word;
  }
`;

const Input = styled.input`
  height: 32px;
  width: 300px;
  background-color: ${({ theme }) => theme.gray4};
  border: 2px solid ${({ theme }) => theme.gray3};
  border-radius: 10px;
  font-size: ${({ theme }) => theme.body};
  font-family: 'Poppins', sans-serif;
  padding: 0 10px;
  margin-top: 5px;
  color: ${({ theme }) => theme.text};
  outline: none;
  transition: border-color 0.15s ease-in-out;

  :focus {
    border-color: ${({ theme }) => theme.gray2};
  }
`;

const Button = styled.button`
  display: block;
  height: 32px;
  padding: 0 16px;
  background-color: ${({ theme, remove }) => (remove ? theme.red : theme.blue)};
  border: none;
  border-radius: 10px;
  color: ${({ theme }) => theme.text};
  font-size: ${({ theme }) => theme.body};
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  outline: none;
  transition: background-color 0.15s ease-in-out;
  margin-top: ${({ noMargin }) => (noMargin ? '0px' : '10px')};

  :hover,
  :focus {
    background-color: ${({ theme, remove }) => (remove ? theme.redHover : theme.blueHover)};
  }

  :disabled {
    opacity: 0.38;
    cursor: default;
  }
`;

const FilesWrapper = styled.div`
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-start;
  padding: 15px;
  width: 100%;
  min-height: calc(100% - 64px);
  height: calc(100% - 64px);
  overflow-y: scroll;

  ::-webkit-scrollbar {
    width: 12px;
    background-color: ${({ theme }) => theme.gray5};
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.gray4};
    border-radius: 2rem;
  }
`;

const Files = styled.div`
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  height: 100%;
  width: ${({ isShrink }) => (isShrink ? 'calc(100% - 400px)' : '100%')};

  ${({ isGrid }) =>
    isGrid
      ? css`
          flex-flow: row wrap;

          button {
            margin-bottom: 5px;
          }

          img,
          div,
          svg {
            height: 64px;
          }

          p {
            max-width: 128px;
          }
        `
      : css`
          flex-flow: column nowrap;

          button {
            width: 100%;
            flex-flow: row nowrap;
            align-items: center;
            margin-bottom: 5px;
          }

          img,
          div,
          svg {
            height: 32px;
          }

          p {
            max-width: 100%;
          }
        `};
`;

const Icon = styled.div`
  max-height: 64px;
  max-width: 64px;
  margin-bottom: 5px;
`;

const Img = styled.img`
  height: auto;
  width: auto;
  max-width: 250px;
  border-radius: 5px;
  margin: 15px auto;
`;

const List = styled.ul`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  list-style-type: none;
  width: 100%;
`;

const ListItem = styled.li`
  display: flex;
  flex-flow: row nowrap;
  width: 100%;

  ${Paragraph}:first-of-type {
    color: ${({ theme }) => theme.textSecondary};
    margin-right: 10px;
    width: 50%;
    text-align: right;
  }
`;

const DownloadButton = styled.button`
  border: none;
  background-color: transparent;
  color: ${({ theme }) => theme.text};
  text-align: center;
  margin-top: auto;
  margin-bottom: 25px;
  padding: 10px;
  border-radius: 10px;
  outline: none;
  cursor: pointer;

  :hover {
    background-color: ${({ theme }) => theme.gray3};
  }

  :focus {
    background-color: ${({ theme }) => theme.blue};
  }

  ${Paragraph} {
    font-size: ${({ theme }) => theme.heading};
  }

  svg {
    fill: ${({ theme }) => theme.text};
    height: 64px;
    width: 64px;
  }
`;

const RemoveButton = styled(DownloadButton)`
  ${Paragraph} {
    color: ${({ theme }) => theme.red};
  }

  svg {
    fill: ${({ theme }) => theme.red};
  }
`;

const ButtonIcon = styled.button`
  position: absolute;
  top: 5px;
  left: 5px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  fill: ${({ theme }) => theme.text};
  height: 32px;
  width: 32px;
  outline: none;
`;

const Anchor = styled(Link)`
  color: ${({ theme }) => theme.blue};
  text-decoration: none;

  :hover {
    text-decoration: underline;
  }
`;

const ButtonGroup = styled.div`
  position: relative;
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  justify-content: space-around;
  width: 100%;
`;

const ToolTip = styled.div`
  position: absolute;
  top: 0;
  left: 10px;
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  width: calc(100% - 20px);
  padding: 10px;
  background-color: ${({ theme }) => theme.gray3};
  border-radius: 10px;
  transform: ${({ edit }) => (edit ? 'translateY(-135px)' : 'translateY(-60px)')};
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.16);
  transition: transform 0.15s ease-in-out, opacity 0.15s ease-in-out;
`;

const Center = styled.div`
  margin: auto;
`;

const Modal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  min-height: 480px;
  max-height: calc(100% - 100px);
  min-width: 50%;
  padding: 48px 15px 15px;
  border-radius: 15px;
  background-color: ${({ theme }) => theme.gray5};
  box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.16);
  overflow-y: scroll;
  z-index: 9;
`;

const DownloadsItem = styled.div`
  background-color: ${({ theme }) => theme.gray4};
  width: 100%;
  margin: 5px 0;
  padding: 5px 10px;
  border-radius: 10px;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.16);
`;

const Background = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 8;
`;

const StyledHeading = styled(Heading)`
  ${({ isAdmin }) =>
    isAdmin &&
    css`
      color: ${({ theme }) => theme.blue};
    `};

  ${({ isAuth }) =>
    isAuth &&
    css`
      color: ${({ theme }) => theme.yellow};
    `}
`;

const StyledName = styled(Heading)`
  overflow: hidden;
  width: 360px;
  height: 68px;
`;

function MainTemplate({
  children,
  isLoading,
  isDetailsVisible,
  setIsDetailsVisible,
  details,
  query,
  setQuery,
  isAdmin,
  isAuthorized,
}) {
  const tooltipRef = useRef(null);
  const modalRef = useRef(null);

  const [currentPage, setCurrentPage] = useState('');
  const [currentFolders, setCurrentFolders] = useState([]);
  const [currentFile, setCurrentFile] = useState({});
  const [isGrid, setIsGrid] = useState(JSON.parse(window.localStorage.getItem('isGrid')));
  const [isTooltip, setIsTooltip] = useState(false);
  const [tooltipType, setTooltipType] = useState(null);
  const [newFileName, setNewFileName] = useState('');
  const [canSave, setCanSave] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const [options, setOptions] = useState([
    {
      value: 'all',
      label: 'Wszystkie pliki',
    },
  ]);
  const [optionValue, setOptionValue] = useState({
    value: 'all',
    label: 'Wszystkie pliki',
  });
  const [isSelectLoading, setIsSelectLoading] = useState(false);
  const [isDownloadsVisible, setIsDownloadsVisible] = useState(false);

  useOutsideClick(tooltipRef, () => setIsTooltip(false));
  useOutsideClick(modalRef, () => setIsDownloadsVisible(false));

  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    document.title = 'Magazyn';
    const f = document.getElementById('favicon');
    f.href = favicon;
  }, []);

  useEffect(() => {
    database.ref('/folders').on('value', (snapshot) => {
      const folders = [];
      const opt = [];

      if (snapshot.val()) {
        Object.keys(snapshot.val()).forEach((key) => {
          folders.push({ name: snapshot.val()[key], key });
          opt.push({ label: snapshot.val()[key], value: key });
        });
      }

      setCurrentFolders(folders);
      setOptions((prevState) => [...prevState, ...opt]);
    });
  }, []);

  useEffect(() => {
    switch (location.pathname) {
      case '/':
        setCurrentPage('Wszystkie pliki');
        break;

      case '/f/ostatnie':
        setCurrentPage('ostatnie 24h');
        break;

      case '/f/dokumenty':
        setCurrentPage('Dokumenty');
        break;

      case '/f/zdjęcia':
        setCurrentPage('Zdjęcia');
        break;

      case '/f/muzyka':
        setCurrentPage('Muzyka');
        break;

      case '/f/wideo':
        setCurrentPage('Wideo');
        break;

      case '/przeslij':
        setCurrentPage('Prześlij');
        break;

      default:
        currentFolders.forEach(({ name, key }) => {
          if (key === location.pathname.replace('/f/', '')) {
            setCurrentPage(name);
          }
        });
        break;
    }
  }, [history, location, currentFolders]);

  useEffect(() => {
    if (details && currentPage !== 'Prześlij') {
      setIsLoadingDetails(true);

      database
        .ref(details)
        .once('value')
        .then((snapshot) => {
          const { name, type, size, lastModified, uploaded, folder } = snapshot.val();
          const obj = { name, type };

          if (isAdmin) {
            const { downloads } = snapshot.val();
            if (downloads) {
              obj.downloads = [];
              Object.keys(downloads).forEach((key) => {
                obj.downloads.push(downloads[key]);
              });
            }
          }

          obj.size = getSizeLabel(size);
          obj.lastModified = getDateFromTs(lastModified);
          obj.uploaded = getDateFromTs(uploaded);

          const folderKey = folder;
          let folderName;
          if (folder) {
            currentFolders.forEach(({ name: n, key }) => {
              if (key === folderKey) {
                folderName = n;
              }
            });
          }

          obj.folderName = folderName;
          obj.folderKey = folderKey;

          storage
            .ref(details)
            .getDownloadURL()
            .then((url) => {
              obj.url = url;
              if (snapshot.val().type.includes('image/')) {
                obj.isImage = true;
              }
              setCurrentFile(obj);
              setIsLoadingDetails(false);
            });
        }).catch(() => {});
    }
  }, [currentFolders, currentPage, details, isAdmin]);

  useEffect(() => {
    if (currentFile.name) {
      setNewFileName(currentFile.name);
    }
  }, [currentFile]);

  useEffect(() => {
    if (newFileName.trim() === currentFile.name || !newFileName.trim().length) {
      setCanSave(false);
    } else {
      setCanSave(true);
    }
  }, [currentFile, newFileName]);

  useEffect(() => {
    if (isGrid !== null) {
      window.localStorage.setItem('isGrid', isGrid);
    } else {
      setIsGrid(true);
    }
  }, [isGrid]);

  const handleDownload = (id, url, name) => {
    if (url) {
      window
        .fetch(url)
        .then((response) => response.blob())
        .then((blob) => {
          const blobURL = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobURL;
          a.style = 'display: none';

          if (name && name.length) a.download = name;
          document.body.appendChild(a);

          let sBrowser;
          const sUsrAg = window.navigator.userAgent;

          if (sUsrAg.indexOf('Firefox') > -1) sBrowser = 'Mozilla Firefox';
          else if (sUsrAg.indexOf('SamsungBrowser') > -1) sBrowser = 'Samsung Internet';
          else if (sUsrAg.indexOf('Opera') > -1 || sUsrAg.indexOf('OPR') > -1) sBrowser = 'Opera';
          else if (sUsrAg.indexOf('Trident') > -1) sBrowser = 'Microsoft Internet Explorer';
          else if (sUsrAg.indexOf('Edge') > -1) sBrowser = 'Microsoft Edge';
          else if (sUsrAg.indexOf('Chrome') > -1) sBrowser = 'Google Chrome/Chromium';
          else if (sUsrAg.indexOf('Safari') > -1) sBrowser = 'Apple Safari';
          else sBrowser = 'unknown';

          database.ref(`${id}/downloads`).push({
            browser: sBrowser,
            ts: new Date().getTime(),
            height: window.screen.height,
            width: window.screen.width,
            isAdmin,
            isAuthorized,
          });

          a.click();
        });
    }
  };

  const handleRemove = () => {
    const storageRef = storage.ref(details);

    const databaseRef = database.ref(details);

    databaseRef.remove().then(() => {
      storageRef.delete().then(() => {
        setIsDetailsVisible(false);
        setCurrentFile({});
      });
    });
  };

  const handleSave = (key, newName) => {
    const databaseRef = database.ref(key);

    databaseRef
      .update({
        name: newName,
      })
      .then(() => {
        const newFile = currentFile;
        newFile.name = newName;
        setCurrentFile(newFile);
      });
  };

  const handleCreateFolder = (inputValue) => {
    setIsSelectLoading(true);

    const pushRef = database.ref('/folders').push();

    pushRef.set(inputValue, () => {
      setOptionValue({
        label: inputValue,
        value: pushRef.key,
      });

      setIsSelectLoading(false);
    });
  };

  const handleMove = () => {
    setIsSelectLoading(true);
    const ref = database.ref(details);

    ref.update(
      {
        folder: optionValue.value,
      },
      () => {
        const newCurrentFile = currentFile;
        newCurrentFile.folderKey = optionValue.value;
        newCurrentFile.folderName = optionValue.label;
        setCurrentFile(newCurrentFile);

        setIsSelectLoading(false);
      },
    );
  };

  return (
    <Container>
      {isDownloadsVisible ? (
        <Background>
          <Modal ref={modalRef}>
            <div>
              <ButtonIcon type="button" onClick={() => setIsDownloadsVisible(false)}>
                <Close />
              </ButtonIcon>
            </div>
            <Column reverse>
              {currentFile.downloads
                ? currentFile.downloads.map(
                    ({ browser, ts, height, width, isAdmin: adm, isAuthorized: isAuth }) => (
                      <DownloadsItem>
                        <StyledHeading isAdmin={adm} isAuth={isAuth}>
                          {getDateFromTs(ts)}
                        </StyledHeading>
                        <Paragraph>{browser}</Paragraph>
                        <Paragraph>
                          {width} x {height}
                        </Paragraph>
                      </DownloadsItem>
                    ),
                  )
                : null}
            </Column>
          </Modal>
        </Background>
      ) : null}
      <Navbar
        isAdmin={isAdmin}
        currentPage={currentPage}
        isGrid={isGrid}
        setIsGrid={setIsGrid}
        query={query}
        setQuery={setQuery}
      />
      <Row>
        <Sidebar currentPage={currentPage} isAdmin={isAdmin} isAuth={isAuthorized} />
        <Content>
          {isLoading ? (
            <Center>
              <Loading />
            </Center>
          ) : (
            <FilesWrapper>
              {currentPage === 'Prześlij' ? (
                children
              ) : (
                <Files isShrink={isDetailsVisible} isGrid={isGrid}>
                  {children}
                </Files>
              )}
              <Details isVisible={isDetailsVisible}>
                {isLoadingDetails ? (
                  <Center>
                    <Loading />
                  </Center>
                ) : (
                  <>
                    <Column>
                      <StyledName>{currentFile.name}</StyledName>
                      <ButtonIcon type="button" onClick={() => setIsDetailsVisible(false)}>
                        <Close />
                      </ButtonIcon>
                      {currentFile.isImage ? (
                        <Img src={currentFile.url} alt="Ikona" />
                      ) : (
                        <Icon>{getFileIcon(currentFile.type)}</Icon>
                      )}
                      <List>
                        <ListItem>
                          <Paragraph>Rozmiar</Paragraph>
                          <Paragraph>{currentFile.size}</Paragraph>
                        </ListItem>
                        <ListItem>
                          <Paragraph>Dodano</Paragraph>
                          <Paragraph>{currentFile.uploaded}</Paragraph>
                        </ListItem>
                        <ListItem>
                          <Paragraph>Edytowano</Paragraph>
                          <Paragraph>{currentFile.lastModified}</Paragraph>
                        </ListItem>
                        {isAdmin && currentFile.downloads ? (
                          <>
                            <ListItem>
                              <Paragraph>Pobrania</Paragraph>
                              <Paragraph>
                                {currentFile.downloads.length}{' '}
                                <button type="button" onClick={() => setIsDownloadsVisible(true)}>
                                  Pokaż
                                </button>
                              </Paragraph>
                            </ListItem>
                          </>
                        ) : null}
                        <ListItem>
                          <Paragraph>Folder</Paragraph>
                          <Paragraph>
                            {currentFile.folderKey !== 'all' ? (
                              <Anchor to={`/f/${currentFile.folderKey}`}>
                                {currentFile.folderName}
                              </Anchor>
                            ) : (
                              'Brak'
                            )}
                          </Paragraph>
                        </ListItem>
                      </List>
                      {isAdmin ? (
                        <>
                          <CreatableSelect
                            isClearable
                            isLoading={isSelectLoading}
                            isDisabled={isSelectLoading}
                            onChange={(newValue) => setOptionValue(newValue)}
                            onCreateOption={handleCreateFolder}
                            options={options}
                            value={optionValue}
                            styles={customStyles}
                            placeholder="Przenieś do..."
                            formatCreateLabel={(val) => `Stwórz folder o nazwie "${val}"`}
                          />
                          <Button
                            type="button"
                            onClick={handleMove}
                            disabled={isSelectLoading || !optionValue}
                          >
                            Przenieś
                          </Button>
                        </>
                      ) : null}
                    </Column>
                    <Column>
                      <ButtonGroup>
                        <ToolTip
                          ref={tooltipRef}
                          isVisible={isTooltip}
                          edit={tooltipType === 'edit'}
                        >
                          {tooltipType === 'edit' ? (
                            <>
                              <Paragraph>Zmień nazwę</Paragraph>
                              <Input
                                type="text"
                                placeholder={currentFile.name}
                                onChange={(e) => setNewFileName(e.target.value)}
                                value={newFileName}
                              />
                              <Button
                                type="button"
                                disabled={!canSave || newFileName.length > 40}
                                onClick={() => handleSave(details, newFileName)}
                              >
                                Zapisz
                              </Button>
                            </>
                          ) : null}

                          {tooltipType === 'download' ? (
                            <Row>
                              <Paragraph>Pobierz ({currentFile.size})</Paragraph>
                              <Button
                                type="button"
                                noMargin
                                onClick={() =>
                                  handleDownload(details, currentFile.url, currentFile.name)
                                }
                              >
                                Pobierz
                              </Button>
                            </Row>
                          ) : null}

                          {tooltipType === 'remove' ? (
                            <Row>
                              <Paragraph>Na pewno? To nieodwracalne</Paragraph>
                              <Button
                                type="button"
                                remove
                                noMargin
                                onClick={() => handleRemove(currentFile)}
                              >
                                Usuń
                              </Button>
                            </Row>
                          ) : null}
                        </ToolTip>
                        <DownloadButton
                          type="button"
                          onClick={() => {
                            setIsTooltip(true);
                            setTooltipType('edit');
                          }}
                        >
                          <Edit />
                          <Paragraph>Edytuj</Paragraph>
                        </DownloadButton>
                        <DownloadButton
                          type="button"
                          onClick={() => {
                            setIsTooltip(true);
                            setTooltipType('download');
                          }}
                        >
                          <Download />
                          <Paragraph>Pobierz</Paragraph>
                        </DownloadButton>
                        {isAdmin ? (
                          <RemoveButton
                            type="button"
                            onClick={() => {
                              setIsTooltip(true);
                              setTooltipType('remove');
                            }}
                          >
                            <Remove />
                            <Paragraph>Usuń</Paragraph>
                          </RemoveButton>
                        ) : null}
                      </ButtonGroup>
                    </Column>
                  </>
                )}
              </Details>
            </FilesWrapper>
          )}
        </Content>
      </Row>
    </Container>
  );
}

MainTemplate.propTypes = {
  setIsDetailsVisible: PropTypes.func.isRequired,
  setQuery: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]),
  isLoading: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isAuthorized: PropTypes.bool,
  isDetailsVisible: PropTypes.bool,
  details: PropTypes.string,
  query: PropTypes.string,
};

MainTemplate.defaultProps = {
  children: null,
  isLoading: true,
  isAdmin: false,
  isAuthorized: false,
  isDetailsVisible: false,
  details: null,
  query: '',
};

export default MainTemplate;
